import dayjs from 'dayjs';

export function getDomain(email: string) {
  if (typeof email !== 'string') {
    throw new Error('Invalid input. Please provide a valid email string.');
  }

  const parts = email.split('@');

  if (parts.length !== 2) {
    return null;
  }

  return parts[1];
}

export const loadFont = (fontName = 'Poppins') => {
  const apiUrl = `https://fonts.googleapis.com/css2?family=${fontName.replace(/ /g, '+')}&display=swap`;
  // Inject the font link dynamically
  const link = document.createElement('link');
  link.href = apiUrl;
  link.rel = 'stylesheet';
  document.head.appendChild(link);

  document.fonts
    .load(`1em ${fontName}`)
    .then(() => {
      console.log(`${fontName} is loaded and ready to be used.`);
    })
    .catch(() => {
      console.error(`Failed to load font: ${fontName}`);
    });
};

export const generateShortLink = (brandName: string, refCode: string) => {
  const sanitizedBrandName = brandName?.replaceAll(' ', '');
  return `${sanitizedBrandName}/${refCode}`;
};

export const getFormattedShortLink = (env: string, shortLink: string) => {
  if (env === 'prod') {
    return `https://gtsrl.io/${shortLink}`;
  } else {
    return `http://${env}.gtsrl.io/${shortLink}`;
  }
};

export const _calculateDieHardFans = (avgComments: number, avgLikes: number) => {
  if (avgLikes === 0) {
    return 0;
  }
  return (avgComments / avgLikes) * 100;
};

export const _remainingTimeInTrial = (freeTrialEnd: number): { fullForm: string; shortForm: string } => {
  const trialEndsAt = dayjs(freeTrialEnd * 1000); // Convert to milliseconds
  const now = dayjs();

  const diffInSeconds = trialEndsAt.diff(now, 'seconds'); // Total seconds left

  if (diffInSeconds <= 0) {
    return {
      fullForm: 'No time',
      shortForm: '0d',
    };
  }

  const diffInDays = Math.floor(diffInSeconds / (24 * 60 * 60)); // Convert to days
  if (diffInDays >= 1) {
    return {
      fullForm: `${diffInDays} Day${diffInDays > 1 ? 's' : ''}`,
      shortForm: `${diffInDays}d`,
    };
  }

  const diffInHours = Math.floor(diffInSeconds / (60 * 60)); // Convert to hours
  if (diffInHours >= 1) {
    return {
      fullForm: `${diffInHours} hour${diffInHours > 1 ? 's' : ''}`,
      shortForm: `${diffInHours}h`,
    };
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60); // Convert to minutes
  if (diffInMinutes >= 1) {
    return {
      fullForm: `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''}`,
      shortForm: `${diffInMinutes}m`,
    };
  }

  return {
    fullForm: `${diffInSeconds} second${diffInSeconds > 1 ? 's' : ''}`,
    shortForm: `${diffInSeconds}s`,
  };
};

export const getIsNonSocialProfile = (profile: any): boolean => {
  if (!profile) return false;

  let hasProfileLink = false;
  if (profile.profile_link) {
    hasProfileLink = profile?.profile_link !== '' || profile?.profile_link !== null;
  }

  let hasSocialLinks = false;
  if (profile.socialLinks) {
    Object?.entries(profile?.socialLinks).forEach(([key]) => {
      if (key === 'instagram' || key === 'tiktok' || key === 'youtube') {
        hasSocialLinks = true;
      }
    });
  }
  return !hasProfileLink && !hasSocialLinks;
};

export const system_templates = [
  {
    _id: '1',
    name: 'Usage on Ads',
    body: "<div>Hi {{first_name}}, </div><div>Your post perfectly aligns with our brand! We'd love to use it in our ad campaigns to promote your experience with a broader audience. Can we get your approval for this?</div>",
  },
  {
    _id: '2',
    name: 'Whitelisted Ads Access',
    body: "<div>Hey {{first_name}}, </div><div><br></div><div>Our team loved your content! Would you allow us to promote it through  whitelisted ads to help it reach more audience?   </div><div><br></div><div>For that, we'll also need access to your FB business manager, I'll follow up with exact steps once you approve.  </div><div><br></div><div> - {{user_name}}</div>",
  },
  {
    _id: '3',
    name: 'Organic Repost',
    body: "<div>Hey {{first_name}}, </div><div>Your post captures exactly what we stand for! We'd love to repost it on our feed to share your style with our followers. Mind if we repost it on our page and tag you?</div>",
  },
  {
    _id: '4',
    name: 'Social Proof on Website',
    body: "<div>Hey {{first_name}}, </div><div>We're updating our website to include organic content around our products, and this post would be a fantastic addition. Can we add your post to our website?</div>",
  },
];

export function timeAgo(createdAt: string): string {
  const now = new Date();
  const createdDate = new Date(createdAt);
  const diffInSeconds = Math.floor((now.getTime() - createdDate.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds}s ago`;
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}hr ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) {
    return `1d ago`;
  }
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks === 1) {
    return `1w ago`;
  }
  if (diffInWeeks < 52) {
    return `${diffInWeeks}w ago`;
  }

  const diffInYears = Math.floor(diffInWeeks / 52);
  if (diffInYears === 1) {
    return `1y ago`;
  }
  return `${diffInYears}y ago`;
}
